import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { formatarCnpj } from '@via-tax/contracts'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { ContextoSessao } from '../tipos'
import { mensagemDeErro, useTRPC } from '../trpc'

/**
 * Seletor de empresa. Trocar de empresa é uma mutação no servidor (grava na
 * sessão) — o front não guarda "empresa ativa" em lugar nenhum.
 */
export function SeletorEmpresa({ contexto }: { contexto: ContextoSessao }) {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const [aberto, setAberto] = useState(false)
  const [filtro, setFiltro] = useState('')
  const caixa = useRef<HTMLDivElement>(null)
  const lista = useQuery(trpc.sessao.empresasDisponiveis.queryOptions())
  const selecionar = useMutation(
    trpc.sessao.selecionarEmpresa.mutationOptions({
      // Tudo que estava em cache era da empresa anterior.
      onSuccess: () => qc.invalidateQueries(),
    }),
  )

  const disponiveis = useMemo(() => lista.data ?? [], [lista.data])
  const ativa = contexto.empresaAtiva

  // Uma empresa só e nenhuma selecionada: seleciona sozinho, é o único caminho possível.
  useEffect(() => {
    if (!ativa && disponiveis.length === 1 && !selecionar.isPending && !selecionar.isError) {
      selecionar.mutate({ empresaId: disponiveis[0]!.id })
    }
  }, [ativa, disponiveis, selecionar])

  useEffect(() => {
    const fora = (e: MouseEvent) => caixa.current && !caixa.current.contains(e.target as Node) && setAberto(false)
    document.addEventListener('mousedown', fora)
    return () => document.removeEventListener('mousedown', fora)
  }, [])

  const filtradas = useMemo(() => {
    const f = filtro.trim().toLowerCase()
    const soCnpj = f.replace(/[^0-9a-z]/g, '').toUpperCase()
    return disponiveis.filter(
      (e) =>
        !f ||
        e.razaoSocial.toLowerCase().includes(f) ||
        (e.nomeFantasia ?? '').toLowerCase().includes(f) ||
        (soCnpj.length >= 2 && e.cnpj.includes(soCnpj)),
    )
  }, [disponiveis, filtro])

  return (
    <div ref={caixa} className="relative min-w-0">
      <button
        type="button"
        onClick={() => setAberto((a) => !a)}
        className="flex min-w-0 max-w-[28rem] items-center gap-3 rounded-md border border-linha px-3 py-1.5 text-left hover:border-tinta-3 focus-visible:outline-2 focus-visible:outline-acao"
        aria-haspopup="listbox"
        aria-expanded={aberto}
      >
        {ativa ? (
          <span className="min-w-0">
            <span className="block truncate text-[13px] font-semibold">{ativa.nomeFantasia || ativa.razaoSocial}</span>
            <span className="num block text-[11px] text-tinta-3">{formatarCnpj(ativa.cnpj)}</span>
          </span>
        ) : (
          <span className="text-[13px] text-tinta-2">
            {disponiveis.length ? 'Selecione a empresa' : 'Nenhuma empresa habilitada'}
          </span>
        )}
        <span className="ml-auto text-tinta-3" aria-hidden>
          ▾
        </span>
      </button>

      {aberto && (
        <div className="absolute left-0 top-full z-40 mt-1 w-[min(28rem,90vw)] rounded-md border border-linha bg-painel shadow-lg">
          <div className="border-b border-linha p-2">
            <input
              autoFocus
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              placeholder={`Buscar entre ${disponiveis.length} empresa(s) por nome ou CNPJ`}
              className="w-full rounded border border-linha bg-fundo px-2.5 py-1.5 text-[13px] focus:border-navy focus:outline-none"
            />
          </div>
          <ul role="listbox" className="max-h-80 overflow-y-auto py-1">
            {filtradas.map((e) => (
              <li key={e.id}>
                <button
                  role="option"
                  aria-selected={ativa?.id === e.id}
                  onClick={() => {
                    selecionar.mutate({ empresaId: e.id })
                    setAberto(false)
                    setFiltro('')
                  }}
                  className={`flex w-full items-baseline gap-3 px-3 py-2 text-left hover:bg-fundo ${ativa?.id === e.id ? 'bg-fundo' : ''}`}
                >
                  <span className="min-w-0 flex-1 truncate text-[13px]">{e.nomeFantasia || e.razaoSocial}</span>
                  <span className="num shrink-0 text-[11px] text-tinta-3">{formatarCnpj(e.cnpj)}</span>
                </button>
              </li>
            ))}
            {!filtradas.length && <li className="px-3 py-4 text-center text-[13px] text-tinta-3">Nenhuma empresa encontrada</li>}
          </ul>
        </div>
      )}
      {selecionar.isError && <p className="absolute left-0 top-full mt-1 text-xs text-alerta">{mensagemDeErro(selecionar.error)}</p>}
    </div>
  )
}
